import React from 'react';
import styled from 'styled-components';
import { Button, Carousel, Row, Col, Typography, Card } from 'antd';
import { ArrowRightOutlined, StarFilled, WifiOutlined, CoffeeOutlined, CarOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { PageFade } from '../components/PageTransition';
import { homePage, rooms } from '../data/hotelData'; // Ensure 'rooms' is imported
import { Helmet } from "react-helmet";

const { Title, Paragraph, Text } = Typography;

// --- Styled Components ---

const HeroSection = styled.section`
  height: 100vh;
  position: relative;
  overflow: hidden;
  
  .ant-carousel, .slick-slider, .slick-list, .slick-track, .slick-slide > div {
    height: 100vh;
  }
`;

const HeroSlide = styled.div`
  height: 100vh;
  background-image: url(${props => props.$bg});
  background-size: cover;
  background-position: center;
  display: flex !important;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  color: white;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(to bottom, rgba(0,0,0,0.3), rgba(0,0,0,0.6));
  }
`;

const HeroContent = styled(motion.div)`
  z-index: 2;
  max-width: 900px;
  padding: 0 20px;

  h1 {
    font-family: 'Playfair Display', serif;
    font-size: 5rem;
    margin-bottom: 1.5rem;
    color: white;
    text-transform: uppercase;
    letter-spacing: 5px;
    line-height: 1.1;
    
    @media (max-width: 768px) { font-size: 3rem; }
  }

  p {
    font-size: 1.3rem;
    margin-bottom: 2.5rem;
    letter-spacing: 3px;
    font-weight: 300;
    text-transform: uppercase;
  }
`;

const Section = styled.div`
  padding: 6rem 10%;
  background: ${props => props.$dark ? '#111' : '#fff'};
  color: ${props => props.$dark ? '#fff' : 'inherit'};
  position: relative;
  
  @media (max-width: 768px) { padding: 4rem 5%; }
`;

// "About" Section with overlapping images
const AboutImageWrapper = styled.div`
  position: relative;
  height: 500px;
  
  .main-img {
    width: 85%;
    height: 100%;
    object-fit: cover;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 1;
  }

  .accent-box {
    position: absolute;
    bottom: -30px;
    right: 0;
    width: 250px;
    height: 250px;
    background: #d4af37;
    z-index: 0;
  }

  @media (max-width: 768px) { height: 350px; }
`;

// Room Preview Cards
const RoomPreviewCard = styled(Link)`
  display: block;
  position: relative;
  height: 400px;
  border-radius: 4px;
  overflow: hidden;
  text-decoration: none;
  color: white;
  
  &:hover img {
    transform: scale(1.1);
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.6s ease;
  }

  .overlay {
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    padding: 2rem;
    background: linear-gradient(to top, rgba(0,0,0,0.8), transparent);
    
    h3 {
      color: white;
      font-family: 'Playfair Display', serif;
      font-size: 1.8rem;
      margin: 0;
    }
    span {
      color: #d4af37;
      text-transform: uppercase;
      font-size: 0.9rem;
      letter-spacing: 1px;
    }
  }
`;

// Feature/Amenity Box
const FeatureBox = styled.div`
  text-align: center;
  padding: 2rem;
  border: 1px solid #eee;
  transition: all 0.3s;
  
  &:hover {
    border-color: #d4af37;
    transform: translateY(-5px);
  }

  .icon {
    font-size: 2.5rem;
    color: #d4af37;
    margin-bottom: 1.5rem;
  }
  
  h4 {
    font-family: 'Playfair Display', serif;
    font-size: 1.4rem;
    margin-bottom: 1rem;
  }
`;

// Parallax "Experience" Section
const ParallaxSection = styled.div`
  height: 60vh;
  background-image: url('https://firebasestorage.googleapis.com/v0/b/miran-suites.firebasestorage.app/o/Home%20Page%2Fpremium-room-with-living-area-03.jpg?alt=media&token=25540d31-5118-4bb1-a21b-64eca100bb4c');
  background-attachment: fixed;
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: rgba(0,0,0,0.5);
  }
`;

// --- Animations ---
const fadeInUp = {
  hidden: { opacity: 0, y: 60 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8 } }
};

// --- Component ---

const Home = () => {
  return (
    <PageFade>
      <Helmet>
        <title>Home | Miran Suites</title>
        <meta name="description" content="Welcome to Miran Suites. Experience the pinnacle of luxury and comfort in our world-class accommodations." />
      </Helmet>

      {/* 1. HERO SECTION */}
      <HeroSection>
        <Carousel autoplay effect="fade" autoplaySpeed={5000}>
          {[homePage?.HeroSlide1, homePage?.HeroSlide2].map((bg, idx) => (
            <HeroSlide key={idx} $bg={bg}>
              <HeroContent
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 1 }}
              >
                <h1>{idx === 0 ? "Miran Suites" : "Luxury Redefined"}</h1>
                <p>{idx === 0 ? "Experience Timeless Elegance" : "Your Sanctuary in the City"}</p>
                <Link to="/rooms">
                  <Button 
                    type="primary" 
                    size="large" 
                    style={{ 
                      background: 'transparent', 
                      borderColor: '#fff', 
                      height: '55px', 
                      padding: '0 50px',
                      fontSize: '1rem',
                      letterSpacing: '2px',
                      borderWidth: '2px'
                    }}
                  >
                    DISCOVER MORE
                  </Button>
                </Link>
              </HeroContent>
            </HeroSlide>
          ))}
        </Carousel>
      </HeroSection>

      {/* 2. INTRO / WELCOME SECTION */}
      <Section>
        <Row gutter={[64, 64]} align="middle">
          <Col xs={24} md={12}>
            <AboutImageWrapper>
              <div className="accent-box"></div>
              <img 
                src={homePage?.Intro} 
                alt="Hotel Interior" 
                className="main-img" 
              />
            </AboutImageWrapper>
          </Col>
          <Col xs={24} md={12}>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
            >
              <Text style={{ color: '#d4af37', letterSpacing: '2px', textTransform: 'uppercase', fontWeight: 'bold' }}>
                Welcome to Miran Suites
              </Text>
              <Title level={2} style={{ fontFamily: 'Playfair Display', fontSize: '3rem', margin: '1rem 0' }}>
                A Legacy of Luxury
              </Title>
              <Paragraph style={{ fontSize: '1.1rem', lineHeight: '1.8', color: '#666', marginBottom: '2rem' }}>
                Nestled in the heart of the city, Miran Suites offers a retreat of unparalleled luxury. 
                Our design philosophy marries contemporary aesthetics with timeless classical touches, 
                creating an atmosphere of sophisticated calm.
              </Paragraph>
              <Link to="/about">
                <Button type="primary" size="large" style={{ background: '#1a1a1a', borderColor: '#1a1a1a', height: '50px', padding: '0 40px' }}>
                  READ OUR STORY
                </Button>
              </Link>
            </motion.div>
          </Col>
        </Row>
      </Section>

      {/* 3. FEATURED ROOMS PREVIEW */}
      <Section $dark>
        <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
          <Text style={{ color: '#d4af37', letterSpacing: '2px', textTransform: 'uppercase' }}>Accommodations</Text>
          <Title level={2} style={{ color: 'white', fontFamily: 'Playfair Display', fontSize: '3rem', marginTop: '10px' }}>
            Stay in Style
          </Title>
        </div>
        
        <Row gutter={[24, 24]}>
          {rooms.slice(0, 3).map((room, index) => (
            <Col xs={24} md={8} key={room.id}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
              >
                <RoomPreviewCard to={`/rooms#${room.id}`}>
                  <img src={room.images[0]} alt={room.title} />
                  <div className="overlay">
                    <span>{room.size}</span>
                    <h3>{room.title}</h3>
                  </div>
                </RoomPreviewCard>
              </motion.div>
            </Col>
          ))}
        </Row>
        
        <div style={{ textAlign: 'center', marginTop: '4rem' }}>
          <Link to="/rooms">
            <Button size="large" ghost style={{ borderColor: '#d4af37', color: '#d4af37', height: '50px', padding: '0 40px' }}>
              VIEW ALL SUITES <ArrowRightOutlined />
            </Button>
          </Link>
        </div>
      </Section>

      {/* 4. PARALLAX BREAK */}
      <ParallaxSection>
        <motion.div 
          initial={{ scale: 0.9, opacity: 0 }}
          whileInView={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.8 }}
          style={{ position: 'relative', zIndex: 2, textAlign: 'center', color: 'white' }}
        >
          <StarFilled style={{ fontSize: '2rem', color: '#d4af37', marginBottom: '1rem' }} />
          <Title style={{ color: 'white', fontFamily: 'Playfair Display', fontSize: '3.5rem', margin: 0 }}>
            Unforgettable Moments
          </Title>
          <Paragraph style={{ color: 'white', fontSize: '1.2rem', marginTop: '1rem' }}>
            Where every detail is curated for your perfection.
          </Paragraph>
        </motion.div>
      </ParallaxSection>

      {/* 5. AMENITIES / SERVICES */}
      <Section>
        <Row gutter={[48, 48]}>
          <Col xs={24} md={8}>
            <FeatureBox>
              <div className="icon"><CoffeeOutlined /></div>
              <h4>Fine Dining</h4>
              <p>Experience culinary masterpieces prepared by our world-class chefs in an elegant setting.</p>
            </FeatureBox>
          </Col>
          <Col xs={24} md={8}>
            <FeatureBox>
              <div className="icon"><WifiOutlined /></div>
              <h4>High-Speed Wi-Fi</h4>
              <p>Stay connected with complimentary high-speed internet access throughout your stay.</p>
            </FeatureBox>
          </Col>
          <Col xs={24} md={8}>
            <FeatureBox>
              <div className="icon"><CarOutlined /></div>
              <h4>Luxury Transport</h4>
              <p>Private chauffeur services available for airport transfers and city tours.</p>
            </FeatureBox>
          </Col>
        </Row>
      </Section>

    </PageFade>
  );
};

export default Home;